import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Textarea,
  Button,
  HStack,
  Box,
  Text,
  Heading,
  useColorModeValue,
  IconButton,
  InputGroup,
  InputRightElement,
  Divider,
  Collapse,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Flex,
  Grid,
  GridItem,
  useBreakpointValue,
  SlideFade,
  Card,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  Icon,
  Link,
  Tooltip,
  Link as ChakraLink
} from '@chakra-ui/react';
import {
  MagnifyingGlass,
  Lightning,
  Check,
  X,
  Article,
  Clock,
  BookOpen,
  PencilSimple,
  ClipboardText,
  TagSimple,
  Notebook,
  PlusCircle,
  Globe,
  Sparkle
} from '@phosphor-icons/react';
import {
  Tag,
  TagLabel,
  TagLeftIcon,
  TagCloseButton,
  Wrap,
  WrapItem,
  Spinner,
  useToast
} from '@chakra-ui/react';
import AIResponseSearchResults from '../../ai/AIResponseSearchResults';
import { useCreateSite } from '../../../context/CreateSiteContext';
import { checkSiteNameExists, searchSites } from '../../../utils/elasticsearchApi';
import { motion } from 'framer-motion';
import SourceLinkedText from '../../site/SourceLinkedText'; // Now used for preview
// import CitationList from '../../site/CitationList'; // Managed by CitationManager
import { processCitations, renumberInTextCitations } from '../../../utils/citationUtils';
import GroundingSources from '../../site/GroundingSources';
import AdventureJournalLoading from '../../loading/AdventureJournalLoading';
import CitationManager from '../utils/CitationManager';
import TextFieldCitationDetector from '../utils/TextFieldCitationDetector';
import ExistingSitesPopup from '../ExistingSitesPopup';

const MotionBox = motion(Box);

// Custom hook to extract all theme values
const useThemeColors = () => {
  return {
    bgColor: useColorModeValue('white', 'gray.800'),
    accentColor: useColorModeValue('yellow.50', 'rgba(214, 173, 96, 0.1)'),
    borderHighlight: useColorModeValue('border.default', 'rgba(139, 109, 89, 0.5)'),
    borderColor: useColorModeValue('yellow.200', 'rgba(214, 173, 96, 0.3)'),
    textColor: useColorModeValue('text.default', 'text.default'),
    mutedColor: useColorModeValue('text.muted', 'text.muted'),
    cardBg: useColorModeValue('bg.surface', 'bg.surface'),
    inputBg: useColorModeValue('bg.surface', 'bg.surface'),
    dividerColor: useColorModeValue('gray.200', 'gray.700')
  };
};

const sectionsConfig = [
  { field: 'overviewIntroduction', title: 'Overview / Introduction', placeholder: 'Enter a brief summary of the site...' },
  { field: 'discoveryAndExcavation', title: 'Discovery and Excavation', placeholder: 'How and when was the site discovered and researched?...' },
  { field: 'historicalContextAndChronology_narrative', title: 'Historical Context & Chronology (Narrative)', placeholder: 'Describe the different periods of occupation and associated cultures...' },
  { field: 'keyArchitecturalFeaturesLayout', title: 'Key Architectural Features / Layout', placeholder: 'Describe important structures and site organization...' },
  { field: 'purposeAndFunction', title: 'Purpose and Function', placeholder: 'Theories about how the site was used...' },
  { field: 'culturalSignificance_narrative', title: 'Cultural Significance (Narrative)', placeholder: 'The importance of the site to the people who built/used it...' },
  { field: 'notableFindsArtifacts_narrative', title: 'Notable Finds / Artifacts (Narrative)', placeholder: 'Significant items discovered at the site...' },
  { field: 'theoriesAndInterpretations', title: 'Theories and Interpretations', placeholder: 'Discussion of different scholarly or alternative views...' },
  { field: 'currentStatusAndPreservation_narrative', title: 'Current Status and Preservation (Narrative)', placeholder: 'The site\'s condition today and conservation efforts...' },
];

export const BasicInfoStep = () => {
  const {
    siteData,
    // setSiteData, // Direct setSiteData is replaced by updateSiteData for better control in context
    setSiteData: updateSiteData, // Use setSiteData from context and alias it to updateSiteData
    validationErrors,
    fetchAiSiteInfo,
    aiLoading,
    groundingInfo,
    searchResults,
    searchQuery,
    citations, // Get citations from context for CitationManager
    // addCitation, // Not directly called from BasicInfoStep
    // updateCitation, // Not directly called from BasicInfoStep
    // deleteCitation, // Not directly called from BasicInfoStep
    setDuplicateNameStatus,
  } = useCreateSite();

  const [isCheckingName, setIsCheckingName] = useState(false);
  const [nameIsDuplicate, setNameIsDuplicate] = useState(false);
  const [duplicateSiteId, setDuplicateSiteId] = useState(null);
  const [showGroundingSources, setShowGroundingSources] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showSpellCheck, setShowSpellCheck] = useState(true);
  const [activeTextFieldId, setActiveTextFieldId] = useState(null);
  const [citationManagerAccordionIndex, setCitationManagerAccordionIndex] = useState(-1); // -1 for closed, 0 for open
  
  const textareaRefs = useRef({});
  const toast = useToast();
  const colors = useThemeColors();
  const { bgColor, accentColor, borderColor, borderHighlight, textColor, mutedColor, cardBg, inputBg, dividerColor } = colors;

  const checkName = useCallback(
    debounce(async (name) => {
      if (!name || name.trim().length < 3) {
        setIsCheckingName(false);
        setNameIsDuplicate(false);
        setDuplicateSiteId(null);
        setDuplicateNameStatus(false);
        return;
      }
      setIsCheckingName(true);
      try {
        // Use direct search instead of the problematic checkSiteNameExists function
        const searchResult = await searchSites({ 
          query: name.trim(), 
          size: 100 
        });
        
        console.log('Site name check - Search result:', searchResult);
        
        let foundExactMatch = false;
        let matchingSiteId = null;
        
        if (searchResult.success && searchResult.data) {
          // Look for exact match (case-insensitive)
          const exactMatch = searchResult.data.find(site => 
            site.name && site.name.toLowerCase() === name.toLowerCase()
          );
          
          if (exactMatch) {
            foundExactMatch = true;
            matchingSiteId = exactMatch.id;
            console.log('Found exact match:', exactMatch.name, 'ID:', exactMatch.id);
          }
        }
        
        setNameIsDuplicate(foundExactMatch);
        setDuplicateSiteId(matchingSiteId);
        setDuplicateNameStatus(foundExactMatch);
        
        console.log('Site name check result:', {
          name,
          foundExactMatch,
          matchingSiteId
        });
        
      } catch (error) {
        console.error('Error checking site name:', error);
        setNameIsDuplicate(false);
        setDuplicateSiteId(null);
        setDuplicateNameStatus(false);
      } finally {
        setIsCheckingName(false);
      }
    }, 500),
    [setDuplicateNameStatus]
  );

  useEffect(() => {
    checkName(siteData.name);
  }, [siteData.name, checkName]);

  // Citations are primarily managed in siteData.citations from context.
  // This derived state is for passing to CitationManager and TextFieldCitationDetector.
  const localCitations = useMemo(() => siteData.citations || [], [siteData.citations]);

  // Helper to extract citation numbers like [1], [23] from text
  const extractCitationNumbers = (text) => {
    if (!text || typeof text !== 'string') return [];
    const regex = /\[(\d+)\]/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(parseInt(match[1], 10));
    }
    return [...new Set(matches)].sort((a, b) => a - b);
  };
  
  const handleChange = useCallback((e) => {
    const { name: field, value: newValue } = e.target;
    
    let newSiteData = { ...siteData };
    let fieldObjectChanged = false;

    if (sectionsConfig.some(sec => sec.field === field)) {
      const currentFieldData = siteData[field];
      let newFieldDataObject;
      if (typeof currentFieldData === 'object' && currentFieldData !== null && currentFieldData.hasOwnProperty('value')) {
        newFieldDataObject = { ...currentFieldData, value: newValue };
      } else {
        const existingMetadata = (typeof currentFieldData === 'object' && currentFieldData?.groundingMetadata) ? currentFieldData.groundingMetadata : {};
        newFieldDataObject = { value: newValue, groundingMetadata: existingMetadata || {} };
      }
      newSiteData[field] = newFieldDataObject;
      fieldObjectChanged = true;
    } else {
      // For simple fields like 'name'
      newSiteData[field] = newValue;
      fieldObjectChanged = true;
    }

    // Consolidate all text fields for citation detection
    let allTextForDetection = newSiteData.name || '';
    sectionsConfig.forEach(secConf => {
      const sectionValue = newSiteData[secConf.field];
      if (typeof sectionValue === 'object' && sectionValue !== null && sectionValue.hasOwnProperty('value')) {
        allTextForDetection += ` ${sectionValue.value || ''}`;
      } else if (typeof sectionValue === 'string') {
        allTextForDetection += ` ${sectionValue}`;
      }
    });
    
    const detectedNumbers = extractCitationNumbers(allTextForDetection);
    const currentCitations = newSiteData.citations || [];
    const updatedCitations = [...currentCitations];
    let citationsArrayChanged = false;

    detectedNumbers.forEach(num => {
      if (!updatedCitations.find(c => c.number === num)) {
        // Requirement 1: Add placeholder with number, empty title/url, and default type
        updatedCitations.push({ number: num, title: '', url: '', type: 'unknown' });
        citationsArrayChanged = true;
      }
    });

    if (citationsArrayChanged) {
      updatedCitations.sort((a, b) => a.number - b.number);
      newSiteData.citations = updatedCitations;
    }
    
    // Only call updateSiteData if something actually changed
    if (fieldObjectChanged || citationsArrayChanged) {
      updateSiteData(newSiteData);
    }

  }, [siteData, updateSiteData]);

  const handleChangeField = useCallback((fieldName, fieldValue) => {
    // Simulate an event object to reuse the main handleChange logic
    const syntheticEvent = {
      target: {
        name: fieldName,
        value: fieldValue,
      },
    };
    handleChange(syntheticEvent);
  }, [handleChange]);

  const handleTagClick = useCallback(async (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
      
      const newQuery = `${siteData.name} ${tag}`;
      
      // Update the site name to trigger the AI call with the new query
      await updateSiteData({ ...siteData, name: newQuery });
      
      // Now, fetch the AI info
      try {
        await fetchAiSiteInfo();
        setShowGroundingSources(true);
      } catch (error) {
        console.error('Error generating info from tag click:', error);
        toast({ title: "Error generating AI info", description: error.message, status: "error", duration: 5000, isClosable: true });
      }
    }
  }, [selectedTags, siteData, fetchAiSiteInfo, updateSiteData, toast]);

  const removeTag = useCallback((tag) => {
    setSelectedTags(selectedTags.filter(t => t !== tag));
  }, [selectedTags]);

  const getAllFeatures = useCallback(() => {
    if (!searchResults || !Array.isArray(searchResults)) return [];
    const featuresSet = new Set();
    searchResults.forEach(site => {
      if (site.features && Array.isArray(site.features)) {
        site.features.forEach(feature => {
          if (feature) featuresSet.add(feature.toLowerCase());
        });
      }
    });
    return [...featuresSet];
  }, [searchResults]);
  
  const handleGenerateInfo = useCallback(async () => {
    try {
      await fetchAiSiteInfo();
      setShowGroundingSources(true);
    } catch (error) {
      console.error('Error generating info:', error);
      toast({ title: "Error generating AI info", description: error.message, status: "error", duration: 5000, isClosable: true });
    }
  }, [fetchAiSiteInfo, setShowGroundingSources, toast]);

  const handleAcceptSpelling = useCallback(() => {
    updateSiteData({
      ...siteData,
      name: siteData.spellCheck?.correctedName
    });
    setShowSpellCheck(false);
  }, [updateSiteData, siteData]);

  const handleRejectSpelling = useCallback(() => {
    setShowSpellCheck(false);
  }, []);

  const registerTextFieldRef = useCallback((id, ref) => {
    if (ref) {
      textareaRefs.current[id] = ref;
    }
  }, []);

  const handleTextFieldFocus = useCallback((fieldId) => {
    setActiveTextFieldId(fieldId);
  }, []);

  const handleInsertCitation = useCallback((citationNumber) => {
    if (!activeTextFieldId || !textareaRefs.current[activeTextFieldId]) {
      toast({ title: "No text field selected", description: "Click into a text field first to insert a citation.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    
    const textarea = textareaRefs.current[activeTextFieldId];
    const { selectionStart, selectionEnd } = textarea;
    const currentValue = textarea.value;
    
    const newValue =
      currentValue.substring(0, selectionStart) +
      `[${citationNumber}]` +
      currentValue.substring(selectionEnd);
    
    const newCursorPosition = selectionStart + `[${citationNumber}]`.length;
    
    const event = { target: { name: activeTextFieldId, value: newValue } };
    handleChange(event); // This will also trigger citation detection
    
    setTimeout(() => {
      if (textareaRefs.current[activeTextFieldId]) {
        const textArea = textareaRefs.current[activeTextFieldId];
        textArea.focus();
        textArea.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  }, [activeTextFieldId, handleChange, toast]);
  
  const handleEditCitation = useCallback((citation) => {
    // Open the citation manager accordion if it's not already open
    setCitationManagerAccordionIndex(0);
    
    // We need to access the CitationManager's handleEditCitation method
    // We simulate this by setting up the form data and opening the edit modal
    // by leveraging the existing CitationManager handleEditCitation function
    setTimeout(() => {
      // This directly calls handleEditCitation in CitationManager
      const citationManagerComponent = document.querySelector('[data-testid="citation-manager"]');
      if (citationManagerComponent && citation) {
        // Find the edit button for this citation
        const editButtons = citationManagerComponent.querySelectorAll('[aria-label="Edit citation"]');
        const citationItems = citationManagerComponent.querySelectorAll('[data-citation-number]');
        
        // Find the matching citation element and click its edit button
        for (let i = 0; i < citationItems.length; i++) {
          const item = citationItems[i];
          const number = item.getAttribute('data-citation-number');
          if (number === String(citation.number) && editButtons[i]) {
            editButtons[i].click();
            break;
          }
        }
      }
    }, 100); // Small delay to ensure the accordion has opened
  }, [setCitationManagerAccordionIndex]);
  
  const handleAddCitation = useCallback(() => {
    setCitationManagerAccordionIndex(0); // Open the accordion
    
    // Wait a moment for the accordion to open, then click the add button
    setTimeout(() => {
      const citationManagerComponent = document.querySelector('[data-testid="citation-manager"]');
      if (citationManagerComponent) {
        const addButton = citationManagerComponent.querySelector('button[aria-label="Add Citation"]');
        if (addButton) {
          addButton.click();
        }
      }
    }, 100);
  }, [setCitationManagerAccordionIndex]);

  // Reference to the citation manager accordion
  const citationManagerRef = useRef(null);
  
  const handleAddOrManageCitations = useCallback(() => {
    // Open the accordion if it's not already open
    setCitationManagerAccordionIndex(0);
    
    // Scroll to the citation manager with a small delay to ensure the accordion is open
    setTimeout(() => {
      if (citationManagerRef.current) {
        citationManagerRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  }, []);
  
  const handleCitationsChange = useCallback((updatedCitations) => {
    // This function is called by CitationManager when citations are added, edited, or deleted.
    // It should update the siteData in the context.
    updateSiteData({ ...siteData, citations: updatedCitations });
  }, [updateSiteData, siteData]);

  const allTextFieldsForManager = useMemo(() => {
    const textFields = {};
    sectionsConfig.forEach(({ field }) => {
      const fieldData = siteData[field];
      // Extract value safely
      const value = (typeof fieldData === 'object' && fieldData !== null && 'value' in fieldData)
        ? fieldData.value || ''
        : (typeof fieldData === 'string' ? fieldData : '');
      textFields[field] = value;
    });
    if (siteData.name) {
        textFields.name = typeof siteData.name === 'string' ? siteData.name : '';
    }
    return textFields;
  }, [
    siteData.name,
    // Add dependencies for *all* text fields included in the object
    ...sectionsConfig.map(({ field }) => {
      const fieldData = siteData[field];
      return (typeof fieldData === 'object' && fieldData !== null && 'value' in fieldData)
        ? fieldData.value
        : (typeof fieldData === 'string' ? fieldData : '');
    })
  ]);
  
  const handleToggleSources = useCallback(() => {
    setShowGroundingSources(prev => !prev);
  }, []);

  const spellCheckAlert = useMemo(() => {
    if (!siteData.spellCheck?.correctedName || !showSpellCheck) return null;
    return (
      <Alert status="info" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" borderRadius="md" p={4} bg={accentColor} borderWidth="1px" borderColor={borderColor} mb={6}>
        <Flex direction="column" align="center">
          <AlertIcon boxSize="24px" mr={0} color="yellow.400" />
          <AlertTitle mt={4} mb={1} fontSize="md">Did you mean "{siteData.spellCheck.correctedName}"?</AlertTitle>
          <AlertDescription maxWidth="sm">We detected a possible spelling correction for your site name.</AlertDescription>
        </Flex>
        <HStack spacing={4} mt={4}>
          <Button leftIcon={<Check />} colorScheme="green" size="sm" onClick={handleAcceptSpelling}>Use Suggestion</Button>
          <Button leftIcon={<X />} variant="ghost" size="sm" onClick={handleRejectSpelling}>Keep Original</Button>
        </HStack>
      </Alert>
    );
  }, [siteData.spellCheck, showSpellCheck, accentColor, borderColor, handleAcceptSpelling, handleRejectSpelling]);

  const groundingSourcesPanel = useMemo(() => {
    if (!groundingInfo) return null;
    return (
      <Collapse in={showGroundingSources} animateOpacity>
        <Box mt={6} mb={4}>
          <GroundingSources groundingMetadata={groundingInfo} siteDescription={siteData.overviewIntroduction?.value || siteData.description || ''} />
        </Box>
      </Collapse>
    );
  }, [groundingInfo, showGroundingSources, siteData.overviewIntroduction, siteData.description]);

  const researchResults = useMemo(() => {
    if (!groundingInfo) return null;
    return (
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} p={4} bg={cardBg} borderRadius="md" borderWidth="2px" borderColor={borderHighlight} position="relative" overflow="hidden" _before={{ content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.05, pointerEvents: 'none', bgImage: 'var(--texture-parchment)', bgRepeat: 'repeat', bgSize: '300px', zIndex: 0 }} mb={4}>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <HStack>
              <BookOpen size={20} color={mutedColor} />
              <Heading size="sm">Research Results</Heading>
            </HStack>
            <Button size="sm" leftIcon={<Article />} variant="ghost" onClick={handleToggleSources}>{showGroundingSources ? 'Hide Sources' : 'Show Sources'}</Button>
          </HStack>
          <Divider />
          <Box>
            {groundingInfo.groundingChunks && (<Badge colorScheme="yellow" mb={2}>{groundingInfo.groundingChunks.length} Sources Found</Badge>)}
            <Text fontSize="sm">Research completed. Information has been added to the form. You can edit any fields as needed.</Text>
          </Box>
        </VStack>
      </MotionBox>
    );
  }, [groundingInfo, cardBg, borderHighlight, mutedColor, handleToggleSources, showGroundingSources]);

  return (
    <Box>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading as="h1" size="lg" mb={2} fontFamily="heading">Basic Information</Heading>
          <Text color={mutedColor}>Start by entering the site name and description</Text>
        </Box>
        
        {spellCheckAlert}
        
        <Grid templateColumns="repeat(12, 1fr)" gap={6}>
          <GridItem colSpan={12}>
            <FormControl isRequired isInvalid={!!validationErrors.name || nameIsDuplicate}>
              <Flex align="center">
                <FormLabel htmlFor="name" fontWeight="medium" mb="0">Site Name</FormLabel>
                <ExistingSitesPopup />
              </Flex>
              <InputGroup>
                <Input 
                  id="name" 
                  name="name" 
                  placeholder="Enter megalithic site name" 
                  value={siteData.name || ''} 
                  onChange={handleChange} 
                  variant="outline" 
                  bg="transparent" 
                  borderWidth="2px" 
                  borderColor={borderHighlight} 
                  _hover={{ bg: "transparent", borderColor: useColorModeValue('border.heavy', 'rgba(139, 109, 89, 0.7)') }} 
                  _focus={{ bg: "transparent", borderColor: 'rgba(139, 109, 89, 0.9)', borderWidth: "2px", boxShadow: `0 0 0 1px rgba(139, 109, 89, 0.9)`, "& ::selection": { background: "rgba(139, 109, 89, 0.3)" } }} 
                  _filled={{ bg: "transparent" }} 
                  sx={{ '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': { WebkitBoxShadow: `0 0 0px 1000px ${bgColor} inset !important`, WebkitTextFillColor: `${textColor} !important`, transition: 'background-color 5000s ease-in-out 0s', }, }} 
                  pr="9rem" // Padding to prevent text from overlapping with the tag
                />
                <InputRightElement width="8.5rem" mr="4px">
                  <Tag
                    size="lg"
                    variant="solid"
                    colorScheme="yellow"
                    borderRadius="full"
                    onClick={!(!siteData.name?.trim() || aiLoading) ? handleGenerateInfo : undefined}
                    cursor={!(!siteData.name?.trim() || aiLoading) ? 'pointer' : 'not-allowed'}
                    opacity={!siteData.name?.trim() || aiLoading ? 0.6 : 1}
                    _hover={{ boxShadow: !(!siteData.name?.trim() || aiLoading) ? 'md' : 'none' }}
                    as="button"
                    disabled={!siteData.name?.trim() || aiLoading}
                    aria-label="Research site information with AI"
                  >
                    <TagLeftIcon as={Sparkle} boxSize="16px" />
                    <TagLabel>{aiLoading ? 'Researching...' : 'Research AI'}</TagLabel>
                  </Tag>
                </InputRightElement>
              </InputGroup>
              {isCheckingName && <FormHelperText>Checking name...</FormHelperText>}
              {nameIsDuplicate && (
                <Alert status="warning" borderRadius="md" mt={2}>
                  <AlertIcon />
                  <Box flex="1">
                    <AlertTitle>Site Name Exists</AlertTitle>
                    <AlertDescription display="block">
                      A site with this name already exists.
                      <ChakraLink as={RouterLink} to={`/site/${duplicateSiteId}`} fontWeight="bold" ml={1} textDecoration="underline" _hover={{ color: "yellow.600" }}>
                        View existing site.
                      </ChakraLink>
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
              {!nameIsDuplicate && <FormHelperText>Enter the common or historical name of the site. {siteData.name && !aiLoading && `AI will search for info about ${siteData.name}.`}</FormHelperText>}
              {validationErrors.name && (<FormErrorMessage>{validationErrors.name}</FormErrorMessage>)}
            </FormControl>
          </GridItem>
          
          {sectionsConfig.map(({ field, title, placeholder }) => (
            <GridItem colSpan={12} key={field}>
              <FormControl mt={4}>
                <FormLabel htmlFor={field} fontWeight="medium">{title}</FormLabel>
                <Textarea id={field} name={field} value={(typeof siteData[field] === 'object' && siteData[field] !== null && siteData[field].hasOwnProperty('value')) ? siteData[field].value || '' : typeof siteData[field] === 'string' ? siteData[field] : ''} onChange={handleChange} placeholder={placeholder} minHeight="100px" bg="transparent" borderWidth="2px" borderColor={borderHighlight} _hover={{ bg: "transparent", borderColor: useColorModeValue('border.heavy', 'rgba(139, 109, 89, 0.7)') }} _focus={{ bg: "transparent", borderColor: 'rgba(139, 109, 89, 0.9)', borderWidth: "2px", boxShadow: `0 0 0 1px rgba(139, 109, 89, 0.9)`, "& ::selection": { background: "rgba(139, 109, 89, 0.3)" } }} _filled={{ bg: "transparent" }} sx={{ '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': { WebkitBoxShadow: `0 0 0px 1000px ${bgColor} inset !important`, WebkitTextFillColor: `${textColor} !important`, transition: 'background-color 5000s ease-in-out 0s', }, }} ref={ref => registerTextFieldRef(field, ref)} onFocus={() => handleTextFieldFocus(field)} />
                <TextFieldCitationDetector text={(typeof siteData[field] === 'object' && siteData[field] !== null) ? siteData[field].value || '' : typeof siteData[field] === 'string' ? siteData[field] : ''} citations={localCitations} onAddCitation={handleAddOrManageCitations} showIfEmpty={activeTextFieldId === field} />
                
                {/* Preview Section Added Here */}
                <Box mt={2} p={2} borderWidth="1px" borderRadius="sm" borderColor={borderColor} bg={accentColor} fontSize="sm">
                  <Text fontSize="xs" color={mutedColor} mb={1}>Preview with Citations:</Text>
                  <SourceLinkedText
                    text={(typeof siteData[field] === 'object' && siteData[field] !== null && siteData[field].hasOwnProperty('value')) ? siteData[field].value || '' : typeof siteData[field] === 'string' ? siteData[field] : ''}
                    citations={siteData.citations || []}
                    groundingMetadata={
                      (siteData[field] && typeof siteData[field] === 'object' && siteData[field].groundingMetadata)
                      ? siteData[field].groundingMetadata
                      : (siteData.groundingMetadata || { groundingSupports: [] })
                    }
                  />
                </Box>
              </FormControl>
            </GridItem>
          ))}
            
          <GridItem colSpan={12}>
            {(searchResults && searchResults.length > 0) && (
              <Box mt={4} position="relative" zIndex={1}>
                <Flex align="center" mb={2}>
                  <MagnifyingGlass size={18} />
                  <Text fontWeight="medium" ml={2}>Related Sites and Features</Text>
                </Flex>
                <Box mb={4}><AIResponseSearchResults searchResults={searchResults} searchQuery={searchQuery} initialLimit={3} /></Box>
                {getAllFeatures().length > 0 && (
                  <Box mt={4}>
                    <Flex align="center" mb={2}><TagSimple size={18} /><Text fontWeight="medium" ml={2}>Suggested Features (click to add to your site)</Text></Flex>
                    <Wrap spacing={2} mt={2}>
                      {getAllFeatures().map((feature, index) => (
                        <WrapItem key={index}><Tag size="md" borderRadius="full" variant="subtle" colorScheme="yellow" cursor="pointer" onClick={() => handleTagClick(feature)}><TagLabel>{feature}</TagLabel></Tag></WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
                {selectedTags.length > 0 && (
                  <Box mt={4}>
                    <Text fontWeight="medium" mb={2}>Selected Features:</Text>
                    <Wrap spacing={2}>
                      {selectedTags.map((tag, index) => (
                        <WrapItem key={index}><Tag size="md" borderRadius="full" variant="solid" colorScheme="green"><TagLabel>{tag}</TagLabel><TagCloseButton onClick={() => removeTag(tag)} /></Tag></WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
              </Box>
            )}
          </GridItem>
        </Grid>
        
        {researchResults}
        {groundingSourcesPanel}
        
        {/* Citation management accordion - Replaces the old display list */}
        <Box mt={6} ref={citationManagerRef} id="citation-manager-section">
          <Accordion allowToggle index={citationManagerAccordionIndex} onChange={(index) => setCitationManagerAccordionIndex(index)}>
            <AccordionItem borderWidth="1px" borderRadius="md">
              <Box as="h2" mb={0}> {/* Ensure h2 is direct child for semantics */}
                <AccordionButton
                  py={3}
                  _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
                  aria-label="Manage Citations Section" // More descriptive
                  aria-expanded={citationManagerAccordionIndex === 0}
                  aria-controls="citation-manager-panel" // Links to panel
                >
                  <HStack flex="1" textAlign="left">
                    <Notebook size={22} />
                    <Text fontWeight="medium">Manage Citations</Text>
                    {/* Use siteData.citations directly from context for the badge count */}
                    {(siteData.citations && siteData.citations.length > 0) && (<Badge borderRadius="full" px={2} colorScheme="blue">{siteData.citations.length}</Badge>)}
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
              </Box>
              <AccordionPanel pb={4} id="citation-manager-panel"> {/* Added id for aria-controls */}
                <Text fontSize="sm" mb={3}>Add, edit, and insert citations. Use [number] format in your text. Markers will be detected, and you can manage full source details here.</Text>
                <CitationManager
                  textFields={allTextFieldsForManager}
                  // Citations prop is no longer needed as CitationManager gets it from context
                  onInsertCitation={handleInsertCitation}
                  data-testid="citation-manager" // Keep for testing
                />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      </VStack>
      <AdventureJournalLoading isOpen={aiLoading} />
    </Box>
  );
};
